import React, { useState } from "react";

import type { Header, RowData } from "@tanstack/react-table-v9";

import { gridSpacing } from "@utils";

import { inColumnWindow } from "./columnWindow";
import HeaderCell from "./HeaderCell";
import { NO_DRAG } from "./layout";
import type { ColumnDragState } from "./layout";
import type { TableV2Features } from "../../features";
import type { VirtualWindow } from "../../hooks/useGridVirtualizer";
import type { TableV2Instance } from "../../hooks/useTableV2";
import { HeaderActions, HeaderGroup, HeaderRow } from "../../styles";
import HeaderActionsTray from "../HeaderActionsTray";

/** Header rows; the action icons sit over the right end of the last one, in a tray when there's more than one */
const GridHeader = <TData extends RowData>({
  table,
  icons,
  columnMenu,
  columnDrag,
  onFit,
  columnWindow,
}: {
  table: TableV2Instance<TData>;
  icons: React.ReactNode[];
  columnMenu: boolean;
  columnDrag: boolean;
  onFit: (columnId: string) => void;
  columnWindow: VirtualWindow | null;
}) => {
  const [drag, setDrag] = useState<ColumnDragState>(NO_DRAG);
  // Side padding plus one 24px icon; extra icons open from the tray over the header
  const iconsWidth = icons.length
    ? gridSpacing[2] + gridSpacing[3] + gridSpacing[5]
    : 0;

  return (
    <HeaderGroup role="rowgroup">
      {table.getHeaderGroups().map((headerGroup, index, groups) => {
        const isLeafRow = index === groups.length - 1;
        const lastId = headerGroup.headers.at(-1)?.id;
        const renderHeader = (
          header: Header<TableV2Features, TData, unknown>,
        ) => (
          <HeaderCell
            key={header.id}
            table={table}
            header={header}
            showMenu={columnMenu}
            allowDrag={columnDrag}
            drag={drag}
            setDrag={setDrag}
            onFit={onFit}
            fixedWidth={!!columnWindow}
            endInset={isLeafRow && header.id === lastId ? iconsWidth : 0}
          />
        );
        return (
          <HeaderRow key={headerGroup.id} role="row">
            {isLeafRow
              ? inColumnWindow(
                  headerGroup.headers,
                  (header) => header.column,
                  columnWindow,
                  renderHeader,
                )
              : headerGroup.headers.map(renderHeader)}
            {isLeafRow && iconsWidth > 0 && (
              <HeaderActions
                style={{ flexBasis: iconsWidth, marginLeft: -iconsWidth }}
              >
                {icons.length > 1 ? <HeaderActionsTray icons={icons} /> : icons}
              </HeaderActions>
            )}
          </HeaderRow>
        );
      })}
    </HeaderGroup>
  );
};

export default GridHeader;
